import React, { useState } from 'react';
import axios from 'axios';
import { Send, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    organization_name: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMessage, setErrorMessage] = useState('');

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.organization_name.trim()) newErrors.organization_name = 'Organization name is required';
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setStatus('loading');
    setErrors({});

    try {
      await axios.post(`${API_BASE_URL}/forms/sandgen-submit`, formData);
      setStatus('success');
      setFormData({ name: '', phone: '', email: '', organization_name: '', message: '' });
    } catch (err) {
      setStatus('error');
      setErrorMessage(err.response?.data?.message || 'Something went wrong. Please try again.');
    }
  };

  return (
    <section id="contact" className="section bg-neutral-50 relative">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-neutral-400 text-sm font-medium uppercase tracking-widest mb-4 block">
              Get In Touch
            </span>
            <h2 className="font-display text-4xl md:text-5xl text-black leading-[1] mb-6 tracking-tight">
              Let's start a
              <br />
              <span className="text-neutral-400">conversation.</span>
            </h2>
            <p className="text-lg text-neutral-500 max-w-lg mx-auto">
              Have questions about Sandgen? Drop us a message and we'll get back to you.
            </p>
          </div>

          {status === 'success' ? (
            <div className="bg-white border border-neutral-200 p-12 text-center">
              <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-black text-xl font-semibold mb-2">Message Sent!</h3>
              <p className="text-neutral-500 mb-8">
                Thank you for reaching out. We'll get back to you shortly.
              </p>
              <button
                onClick={() => setStatus('idle')}
                className="text-black font-medium text-sm hover:text-neutral-600 transition-colors"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white border border-neutral-200 p-8 md:p-12">
              <div className="space-y-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="text-sm font-medium text-black mb-2 block">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    className={`w-full px-4 py-3 bg-neutral-50 border rounded-lg text-sm text-black placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all ${errors.name ? 'border-red-400' : 'border-neutral-200'
                      }`}
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1.5">{errors.name}</p>}
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="text-sm font-medium text-black mb-2 block">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Your phone number"
                    className={`w-full px-4 py-3 bg-neutral-50 border rounded-lg text-sm text-black placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all ${errors.phone ? 'border-red-400' : 'border-neutral-200'
                      }`}
                  />
                  {errors.phone && <p className="text-red-500 text-xs mt-1.5">{errors.phone}</p>}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="text-sm font-medium text-black mb-2 block">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className={`w-full px-4 py-3 bg-neutral-50 border rounded-lg text-sm text-black placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all ${errors.email ? 'border-red-400' : 'border-neutral-200'
                      }`}
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1.5">{errors.email}</p>}
                </div>

                {/* Organization Name */}
                <div>
                  <label htmlFor="organization_name" className="text-sm font-medium text-black mb-2 block">
                    Organization Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="organization_name"
                    name="organization_name"
                    type="text"
                    value={formData.organization_name}
                    onChange={handleChange}
                    placeholder="Your company or organization"
                    className={`w-full px-4 py-3 bg-neutral-50 border rounded-lg text-sm text-black placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all ${errors.organization_name ? 'border-red-400' : 'border-neutral-200'
                      }`}
                  />
                  {errors.organization_name && (
                    <p className="text-red-500 text-xs mt-1.5">{errors.organization_name}</p>
                  )}
                </div>

                {/* Message (optional) */}
                <div>
                  <label htmlFor="message" className="text-sm font-medium text-black mb-2 block">
                    Message <span className="text-neutral-400 text-xs font-normal">(optional)</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your needs..."
                    rows={4}
                    className="w-full px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-lg text-sm text-black placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all resize-none"
                  />
                </div>
              </div>

              {/* Error message */}
              {status === 'error' && (
                <div className="mt-4 flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {errorMessage}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={status === 'loading'}
                className="mt-8 w-full bg-black text-white px-8 py-4 rounded-full font-medium hover:bg-neutral-800 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === 'loading' ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <Send className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
